import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useProfileData } from "../../../hooks/useProfileData";

import { Notification } from "../types/notification";
import { NotificationCategoryEnum } from "../enums/notifications";
import { useGetInfiniteNotifications } from "../api/queries/useGetInfiniteNotifications";
import { updateLastSeenNotifications } from "../api/services/notifications";
import { useGetLastSeenNotification } from "../api/queries/getLastSeenNotification";
import { NOTIFICATIONS_QUERY_KEYS } from "../api/queryKeys";

export const useNotificationsHandler = () => {
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeTab, setActiveTab] = useState<NotificationCategoryEnum>(NotificationCategoryEnum.GENERAL);
  const queryClient = useQueryClient();
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useGetInfiniteNotifications({ category: activeTab });
  const { data: userData } = useProfileData();
  const { id: userId } = userData;

  const { data: lastSeenData, isLoading: isLoadingLastSeenNotification } = useGetLastSeenNotification();

  const categoryLastSeen = lastSeenData?.categoriesLastSeen?.[activeTab] || lastSeenData?.lastSeen || 0;

  const hasNewNotifications =
    !isLoadingLastSeenNotification &&
    Boolean(
      data?.pages?.some((page: any) =>
        page.data.items.some((notification: Notification) => notification.createdAt > categoryLastSeen)
      )
    );

  const handleOpenNotifications = async (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    queryClient.invalidateQueries({ queryKey: [...NOTIFICATIONS_QUERY_KEYS.LAST_SEEN_NOTIFICATION] });
    setNotificationsOpen(true);
  };

  const handleCloseNotifications = async () => {
    setNotificationsOpen(false);
    setAnchorEl(null);
    try {
      await updateLastSeenNotifications({ userId, category: "general" });
    } catch (error) {
      console.error("Failed to update last seen notifications", error);
    }
  };

  return {
    notificationsOpen,
    anchorEl,
    activeTab,
    lastSeen: categoryLastSeen,
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    setActiveTab,
    handleOpenNotifications,
    handleCloseNotifications,
    hasNewNotifications,
  };
};
